// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import EventEmitter from 'events';
import { AbstractViewModel } from '@front/webmobile/ViewModels/AbstractViewModel';
import { AccountMenuItemsHelper } from '@shared/commons/AccountWidget/AccountMenuItemsHelper';
import { AccountWidgetAccountDetailsPanelController } from '@shared/commons/AccountWidget/AccountWidgetAccountDetailsPanelController';
import { AccountWidgetAccountsController } from '@shared/commons/AccountWidget/AccountWidgetAccountsController';
import { AccountWidgetAssetsController } from '@shared/commons/AccountWidget/AccountWidgetAssetsController';
import { AccountWidgetCoordinates } from '@shared/commons/AccountWidget/AccountWidgetCoordinates';
import { Account } from '@shared/commons/cache/Account';
import { ConvertingOldSettings } from '@shared/commons/cache/VerticalPanel/ConvertingOldSettings';
import { VerticalDetailsTypePointer } from '@shared/commons/cache/VerticalPanel/VerticalDetailsTypePointer';
import { CustomerAccess } from '@shared/commons/CustomerAccess/CustomerAccess';
import { DataCache } from '@shared/commons/DataCache';
import { DynProperty } from '@shared/commons/DynProperty';
import { Rectangle } from '@shared/commons/Geometry';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { Resources } from '@shared/localizations/Resources';
import { getAccountModeText } from '@shared/utils/cache/Accounts/accounts.utils';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { AccountDetailsGroupsEnum, GeneralGroupEnum, MarginGroupEnum } from '@shared/utils/Enums/AccountDetailsEnum';
import { MenuIcons } from '@shared/utils/Enums/MenuIcons';
import { externalLinkManager } from '@shared/utils/Managers/ExternalLinkManager';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { Control } from '../elements/Control';
import { LinkedSystem } from '../misc/LinkedSystem';
import { PANEL_ASSET_BALANCES_KEY } from '../panels/AssetBalancesPanel';
import { CloseAccountScreen } from '../screen/CloseAccountScreen';
import { TerceraChangePasswordScreen } from '../screen/TerceraChangePasswordScreen';
import { WithdrawalScreen } from '../screen/WithdrawalScreen';
import { PanelNames, ScreensNames } from '../UtilsClasses/FactoryConstants';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { MENU_TOP_OFFSET } from '../UtilsClasses/SizeConstants';
import { WorkSpaceManager } from '../WorkSpace/WorkSpaceManager';
export var AccountWidgetEvents;
(function (AccountWidgetEvents) {
    AccountWidgetEvents["UpdateWidgetItemsData"] = "updateWidgetItemsData";
    AccountWidgetEvents["UpdateWidgetItemsRelations"] = "updateWidgetItemsRelations";
    AccountWidgetEvents["ValidateWidgetItems"] = "validateWidgetItems";
    AccountWidgetEvents["UpdateWidgetDragAndDropPosition"] = "updateWidgetDragAndDropPosition";
    AccountWidgetEvents["UpdateWidgetDragAndDropData"] = "updateWidgetDragAndDropData";
    AccountWidgetEvents["UpdateRelationOfAccountDetailsPanel"] = "updateRelationOfAccountDetailsPanel";
})(AccountWidgetEvents || (AccountWidgetEvents = {}));
export class AccountWidgetProps {
}
export class AccountWidgetComponentView {
    constructor() {
        this.hovered = false;
        this.visible = false;
        this.tilesTypes = new Map();
        this.accWidgetBtnSelectedClass = '';
        this.contextMenuRef = null;
    }
}
export class AccountWidgetViewModel extends AbstractViewModel {
    get hovered() {
        return this.componentView.hovered;
    }
    set hovered(value) {
        this.updateComponentView('hovered', value);
    }
    get visible() {
        return this.componentView.visible;
    }
    set visible(value) {
        this.updateComponentView('visible', value);
    }
    get AccountTooltip() {
        return this.componentView.AccountTooltip;
    }
    set AccountTooltip(value) {
        this.updateComponentView('AccountTooltip', value);
    }
    get AccountValue() {
        return this.componentView.AccountValue;
    }
    set AccountValue(value) {
        this.updateComponentView('AccountValue', value);
    }
    get AccountModeStr() {
        return this.componentView.AccountModeStr;
    }
    set AccountModeStr(value) {
        this.updateComponentView('AccountModeStr', value);
    }
    get InstrumentType() {
        return this.componentView.InstrumentType;
    }
    set InstrumentType(value) {
        this.updateComponentView('InstrumentType', value);
    }
    get tilesTypes() {
        return this.componentView.tilesTypes;
    }
    set tilesTypes(value) {
        this.updateComponentView('tilesTypes', value);
    }
    get accWidgetBtnSelectedClass() {
        return this.componentView.accWidgetBtnSelectedClass;
    }
    set accWidgetBtnSelectedClass(value) {
        this.updateComponentView('accWidgetBtnSelectedClass', value);
    }
    get DragNDropInProcess() {
        return this.componentView.DragNDropInProcess;
    }
    set DragNDropInProcess(value) {
        this.updateComponentView('DragNDropInProcess', value);
    }
    get detailsGroupItemStateSetting() {
        return this.componentView.detailsGroupItemStateSetting;
    }
    set detailsGroupItemStateSetting(value) {
        this.updateComponentView('detailsGroupItemStateSetting', value);
    }
    get accountId() {
        return this.componentView.accountId;
    }
    set accountId(value) {
        this.updateComponentView('accountId', value);
    }
    get clickedRowTypePointer() {
        return this.componentView.clickedRowTypePointer;
    }
    set clickedRowTypePointer(value) {
        this.updateComponentView('clickedRowTypePointer', value);
    }
    get contextMenuRef() {
        return this.componentView.contextMenuRef;
    }
    set contextMenuRef(value) {
        this.updateComponentView('contextMenuRef', value);
    }
    constructor() {
        super(AccountWidgetComponentView);
        this.eventEmitter = new EventEmitter();
        this.Properties = () => {
            const properties = [];
            this.tilesTypes.forEach((pointer, key) => {
                properties.push(new DynProperty(this.getPropKey(key), pointer.toString(), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
            });
            properties.push(new DynProperty('accountId', this.accountId, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
            const accLinkingActive = LinkedSystem.accLinkingActive;
            properties.push(new DynProperty('accLinkingActive', accLinkingActive, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
            const asset = AccountWidgetAssetsController.SelectedAsset;
            if (!isNullOrUndefined(asset)) {
                properties.push(new DynProperty('assetComboBoxValue', asset.Id, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP));
            }
            return properties;
        };
        this.onAccountChanged = (newAccount, oldAccount) => {
            if (isNullOrUndefined(newAccount))
                return;
            if (Account.IsEqualAccount(newAccount, oldAccount)) {
                return;
            }
            if (!isNullOrUndefined(oldAccount)) {
                oldAccount.OnUpdateAssetBalanceData.UnSubscribe(this.AccountPanelReinit, this);
                oldAccount.OnDayTraderPatternChanged.UnSubscribe(this.AccountPanelReinit, this);
            }
            if (!isNullOrUndefined(newAccount)) {
                newAccount.OnUpdateAssetBalanceData.Subscribe(this.AccountPanelReinit, this);
                newAccount.OnDayTraderPatternChanged.Subscribe(this.AccountPanelReinit, this);
                const AccName = newAccount.toString();
                this.AccountTooltip = AccName;
                this.AccountValue = AccName;
                this.AccountModeStr = getAccountModeText(newAccount);
                this.InstrumentType = newAccount.PreferredInstrumentType;
                this.accountId = newAccount.AcctNumber;
                AccountWidgetAccountDetailsPanelController.setDataSourceAccount(newAccount);
            }
            const accountStr = newAccount.BstrAccount;
            if (LinkedSystem.accLinkingActive) {
                LinkedSystem.accLinkingOn(accountStr);
            }
            this.UpdateTilesDataProvider();
            SessionSettings.save();
        };
        this.onContextMenuInitialized = this.onContextMenuInitialized.bind(this);
    }
    initialize(props) {
        super.initialize(props);
        WorkSpaceManager.OnWorkSpaceChanged.Subscribe(this.AccountPanelReinit, this);
        Control.Ticker.Subscribe(this.UpdateData, this);
        AccountWidgetAccountsController.subscribeOnAccountChanged(this.onAccountChanged);
        AccountWidgetAssetsController.subscribeOnAssetChanged(SessionSettings.save);
        AccountWidgetAccountDetailsPanelController.onTableMouseUP = (hittestInfo) => {
            this.onTableMouseUP(hittestInfo);
        };
        AccountWidgetAccountDetailsPanelController.onTableMouseDown = (typePointer) => {
            this.onTableMouseDown(typePointer);
        };
        AccountWidgetAccountDetailsPanelController.onTableMouseMove = (hittestInfo) => {
            this.onTableMouseMove(hittestInfo);
        };
        AccountWidgetAccountDetailsPanelController.onPanelOpen = () => {
            this.hovered = true;
        };
        AccountWidgetAccountDetailsPanelController.onPanelClose = () => {
            this.hovered = false;
        };
        AccountWidgetAccountDetailsPanelController.onRePopulatedPanel.Subscribe(this.UpdateTilesDataProvider, this);
        AccountWidgetAccountDetailsPanelController.HideAccountDetailsPanel.Subscribe(this.setAccountPanelClosed, this);
        this.deactivateHandler = () => {
            this.activateBtn(false);
        };
        MainWindowManager.MainWindow.onDisconnectCalled.Subscribe(this.deactivateHandler, this);
        MainWindowManager.MainWindow.onMouseMoveEvt.Subscribe(this.onMouseMoveEvtProcess, this);
        document.addEventListener('mouseleave', () => {
            this.hideAccountDetailsPanel();
            this.stopTimer();
        });
        this.setDefaultTilesTypes();
        this.AccountPanelReinit();
        if (!isNullOrUndefined(props.properties))
            this.callBack(props.properties);
        SessionSettings.AdditionalPropertiesHandlers.push(this.Properties);
    }
    dispose() {
        WorkSpaceManager.OnWorkSpaceChanged.UnSubscribe(this.AccountPanelReinit, this);
        Control.Ticker.UnSubscribe(this.UpdateData, this);
        DataCache.OnReintialize.UnSubscribe(this.AccountPanelReinit, this);
        AccountWidgetAccountsController.unsubscribeOnAccountChanged(this.onAccountChanged);
        AccountWidgetAssetsController.unsubscribeOnAssetChanged(SessionSettings.save);
        AccountWidgetAccountDetailsPanelController.onRePopulatedPanel.UnSubscribe(this.UpdateTilesDataProvider, this);
        AccountWidgetAccountDetailsPanelController.HideAccountDetailsPanel.UnSubscribe(this.setAccountPanelClosed, this);
        AccountWidgetAccountsController.clean();
        AccountWidgetAssetsController.clean();
        MainWindowManager.MainWindow.onDisconnectCalled.UnSubscribe(this.deactivateHandler, this);
        MainWindowManager.MainWindow.onMouseMoveEvt.UnSubscribe(this.onMouseMoveEvtProcess, this);
        const propertiesIndex = SessionSettings.AdditionalPropertiesHandlers.indexOf(this.Properties);
        if (propertiesIndex !== -1) {
            SessionSettings.AdditionalPropertiesHandlers.splice(propertiesIndex, 1);
        }
        super.dispose();
    }
    update(props, prevProps) {
        if (isNullOrUndefined(prevProps === null || prevProps === void 0 ? void 0 : prevProps.properties) && props.properties !== (prevProps === null || prevProps === void 0 ? void 0 : prevProps.properties)) {
            this.callBack(props.properties);
        }
    }
    callBack(newProperties) {
        var _a;
        const tileKeys = this.tilesTypes.keys();
        for (const itemKey of tileKeys) {
            this.callBackWidgetItem(itemKey, newProperties);
        }
        this.accountId =
            (_a = DynProperty.getPropValue(newProperties, 'accountId')) !== null && _a !== void 0 ? _a : DataCache.getPrimaryAccount().AcctNumber;
        const curAccount = DataCache.GetAccountById(this.accountId);
        AccountWidgetAssetsController.Account = AccountWidgetAccountsController.Account =
            curAccount !== null && curAccount !== void 0 ? curAccount : DataCache.MainAccountNew;
        this.assetComboBoxValue = DynProperty.getPropValue(newProperties, 'assetComboBoxValue');
        LinkedSystem.accLinkingActive =
            DynProperty.getPropValue(newProperties, 'accLinkingActive') || DataCache.EnableForceLinkingByAccount();
        this.detailsGroupItemStateSetting = DynProperty.getPropValue(newProperties, 'DetailsGroupItemStateMap');
        this.UpdateDataRelation();
    }
    UpdateDataRelation() {
        var _a;
        this.emitEvent(AccountWidgetEvents.UpdateRelationOfAccountDetailsPanel);
        if (!isNullOrUndefined(this.assetComboBoxValue)) {
            AccountWidgetAccountDetailsPanelController.raiseonAssetSet(this.assetComboBoxValue);
        }
        if (!isNullOrUndefined(WorkSpaceManager.currentWorkspace)) {
            this.activateBtn(LinkedSystem.accLinkingActive);
            this.activateLinkedSystem(LinkedSystem.accLinkingActive, (_a = AccountWidgetAccountsController.Account) === null || _a === void 0 ? void 0 : _a.BstrAccount);
        }
        this.emitEvent(AccountWidgetEvents.UpdateWidgetItemsRelations);
    }
    UpdateData() {
        if (!DataCache.Loaded) {
            return;
        }
        this.emitEvent(AccountWidgetEvents.UpdateWidgetItemsData);
    }
    AccountPanelReinit() {
        if (!DataCache.Loaded) {
            DataCache.OnReintialize.Subscribe(this.AccountPanelReinit, this);
            return;
        }
        this.visible = CustomerAccess.panelAllowed(PanelNames.NewAccountDetailsPanel);
        // this.callBack(this.Properties());
    }
    onTableMouseUP(hittestInfo) {
        this.DragNDropInProcess = false;
    }
    onTableMouseDown(typePointer) {
        if (isNullOrUndefined(typePointer)) {
            return null;
        }
        this.clickedRowTypePointer = typePointer;
    }
    onTableMouseMove(hittestInfo) {
        if (isNullOrUndefined(this.clickedRowTypePointer)) {
            return;
        }
        if (!this.DragNDropInProcess) {
            this.DragNDropInProcess = true;
        }
    }
    ShowAccountDetailsPanel(coordinates, accountDetailsPanelNode) {
        this.isAccountDetailsPanelOpen = true;
        AccountWidgetAccountDetailsPanelController.doShowAccountDetailsPanel(coordinates);
    }
    TryToClosePanel(mouseEvent) {
        if (this.isAccountDetailsPanelShouldClose(mouseEvent)) {
            this.hideAccountDetailsPanel();
        }
    }
    isAccountDetailsPanelShouldClose(mouseContext) {
        if (!this.isAccountDetailsPanelOpen) {
            return false;
        }
        return (!this.isMouseOnInfoBlock &&
            !AccountWidgetAccountDetailsPanelController.isMouseOnPanel() &&
            !this.rectangleBtwBlockAndPanel.Contains(mouseContext.event.clientX, mouseContext.event.clientY));
    }
    subscribeEvent(event, callback) {
        this.eventEmitter.on(event, callback);
    }
    unsubscribeEvent(event, callback) {
        this.eventEmitter.off(event, callback);
    }
    emitEvent(event, ...args) {
        this.eventEmitter.emit(event, ...args);
    }
    // replace context with event
    accWidgetBtnContainerOver(btnElement) {
        if (this.DragNDropInProcess || !isNullOrUndefined(this.contextMenuRef)) {
            return;
        }
        const callBack = () => {
            this.showContextMenu(btnElement);
        };
        this.installTimer(callBack);
    }
    accWidgetBtnContainerLeave(event) {
        if (this.DragNDropInProcess) {
            return;
        }
        const tooltip = document.querySelector('.ractive-tooltip');
        if (tooltip === event.relatedTarget) {
            return;
        }
        this.stopTimer();
    }
    accWidgetBtnMouseDown() {
        var _a;
        if (this.DragNDropInProcess) {
            return;
        }
        if (DataCache.EnableForceLinkingByAccount()) {
            return;
        }
        const accBtnActive = this.accWidgetBtnSelectedClass === '';
        this.activateBtn(accBtnActive);
        this.activateLinkedSystem(accBtnActive, (_a = AccountWidgetAccountsController.Account) === null || _a === void 0 ? void 0 : _a.BstrAccount);
    }
    accWidgetInfoBlockContainerOver(event, accountWidgetInfoDiv, accountDetailsPanelNode) {
        this.isMouseOnInfoBlock = true;
        if (this.isAccountDetailsPanelOpen) {
            return null;
        }
        const callBack = () => {
            const rect = accountWidgetInfoDiv.getBoundingClientRect();
            const x = rect.left;
            const y = 60; // Fixed value to show panel below account widget
            const btnHeight = rect.height - 1;
            const yForPanel = rect.height + rect.top + MENU_TOP_OFFSET;
            this.rectangleBtwBlockAndPanel = new Rectangle(x, rect.top + btnHeight, rect.width, y - btnHeight);
            const coordinates = new AccountWidgetCoordinates(x, yForPanel);
            this.ShowAccountDetailsPanel(coordinates, accountDetailsPanelNode);
        };
        this.installTimer(callBack);
    }
    accWidgetInfoBlockContainerLeave(containerContext) {
        this.isMouseOnInfoBlock = false;
        this.stopTimer();
    }
    itemDragNDropEnd(itemKey) {
        this.tilesTypes.set(itemKey, this.clickedRowTypePointer);
        SessionSettings.save();
    }
    showContextMenu(targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const items = this.createMenuItems();
        const x = rect.x;
        const y = rect.bottom + 4;
        showContextMenu({
            x,
            y,
            items,
            functions: {
                onContextMenuInitialized: this.onContextMenuInitialized,
                closeCallback: () => {
                    this.contextMenuRef = null;
                }
            }
        });
    }
    UpdateTilesDataProvider() {
        this.tilesTypes.forEach((value, key) => {
            this.setFirstVisibleItemUnless(value);
        });
        this.emitEvent(AccountWidgetEvents.ValidateWidgetItems);
    }
    setFirstVisibleItemUnless(tileTypePointer) {
        var _a;
        let controller = AccountWidgetAccountDetailsPanelController.getDataSource(tileTypePointer.GroupType);
        while (controller.isHiddenItemRow(tileTypePointer.ItemType) ||
            !((_a = controller.getItemFormattedValue(tileTypePointer.ItemType, tileTypePointer.Key)) === null || _a === void 0 ? void 0 : _a.isValid())) {
            const groupItemsTypes = controller.getGroupItemsTypes();
            const currentIndex = groupItemsTypes.indexOf(tileTypePointer.ItemType);
            if (currentIndex === -1 || currentIndex + 1 >= groupItemsTypes.length) {
                // Move to the next group type if at the end or invalid
                controller = AccountWidgetAccountDetailsPanelController.getDataSource(++tileTypePointer.GroupType);
                if (isNullOrUndefined(controller))
                    break;
                tileTypePointer.ItemType = controller.getGroupItemsTypes()[0];
                tileTypePointer.Key = null;
            }
            else {
                // Move to the next item in the group
                tileTypePointer.ItemType = groupItemsTypes[currentIndex + 1];
                tileTypePointer.Key = null;
            }
        }
    }
    installTimer(callBack) {
        if (isNullOrUndefined(this.timeoutId)) {
            this.startTimer(callBack, 0);
        }
        else {
            this.stopTimer();
            this.startTimer(callBack, 500);
        }
    }
    startTimer(callBack, delay) {
        this.timeoutId = setTimeout(() => {
            callBack();
            this.timeoutId = null;
        }, delay);
    }
    stopTimer() {
        if (!isNullOrUndefined(this.timeoutId)) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }
    activateBtn(value) {
        //NOSONAR[typescript:S2301]
        if (value) {
            this.accWidgetBtnSelectedClass = 'selected';
        }
        else {
            this.accWidgetBtnSelectedClass = '';
        }
    }
    activateLinkedSystem(value, accountStr) {
        LinkedSystem.accLinkingActive = value;
        if (value) {
            LinkedSystem.accLinkingOn(accountStr);
        }
        else {
            LinkedSystem.accLinkingOff();
        }
    }
    onMouseMoveEvtProcess(event) {
        this.TryToClosePanel(event);
        this.emitEvent(AccountWidgetEvents.UpdateWidgetDragAndDropPosition, event);
    }
    hideAccountDetailsPanel() {
        AccountWidgetAccountDetailsPanelController.doHideAccountDetailsPanel();
    }
    setAccountPanelClosed() {
        this.isAccountDetailsPanelOpen = false;
    }
    getPropKey(itemKey) {
        return `${itemKey}TileTypePointer`;
    }
    getOldPropKey(itemKey) {
        return `${itemKey}RowId`;
    }
    callBackWidgetItem(itemKey, newProperties) {
        const dp = DynProperty.getPropValue(newProperties, this.getPropKey(itemKey));
        if (!isNullOrUndefined(dp)) {
            let pointer = ConvertingOldSettings.migrateOldGroupItemEnum(dp);
            if (isNullOrUndefined(pointer)) {
                pointer = VerticalDetailsTypePointer.fromJSON(dp);
            }
            if (!isNullOrUndefined(pointer)) {
                this.tilesTypes.set(itemKey, pointer);
            }
        }
        const dpOld = DynProperty.getPropValue(newProperties, this.getOldPropKey(itemKey));
        if (!isNullOrUndefined(dpOld)) {
            const oldParams = ConvertingOldSettings.ConvertingSettingsFromOldToNew(dpOld);
            if (!isNullOrUndefined(oldParams)) {
                this.tilesTypes.set(itemKey, oldParams);
            }
        }
    }
    setDefaultTilesTypes() {
        this.tilesTypes.set(this.getPropKey('first'), new VerticalDetailsTypePointer(GeneralGroupEnum.CurBalance, AccountDetailsGroupsEnum.General));
        this.tilesTypes.set(this.getPropKey('second'), new VerticalDetailsTypePointer(GeneralGroupEnum.Balance, AccountDetailsGroupsEnum.General));
        this.tilesTypes.set(this.getPropKey('third'), new VerticalDetailsTypePointer(MarginGroupEnum.MaintMargin, AccountDetailsGroupsEnum.Margin));
    }
    createMenuItems() {
        let items = this.getAccountsMenuItems();
        const panelsMenuItems = this.getPanelsMenuItems();
        if (panelsMenuItems.length > 0) {
            if (items.length > 0) {
                items.push({ type: NewContextMenuItemType.Separator });
            }
            items = items.concat(panelsMenuItems);
        }
        const externallinkMenuItems = this.getExternalLinksMenuItems();
        if (externallinkMenuItems.length > 0) {
            if (items.length > 0) {
                items.push({ type: NewContextMenuItemType.Separator });
            }
            items = items.concat(externallinkMenuItems);
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_CLOSE_ACCOUNT)) {
            if (items.length > 0) {
                items.push({ type: NewContextMenuItemType.Separator });
            }
            items.push({
                type: NewContextMenuItemType.Default,
                icon: MenuIcons.CloseAccount,
                text: 'ribbon.closeAccount',
                event: () => {
                    CloseAccountScreen.ShowMsg();
                }
            });
        }
        return items;
    }
    getAccountsMenuItems() {
        const accountClickEvent = (item) => {
            AccountWidgetAccountsController.Account = item.value;
            if (LinkedSystem.accLinkingActive) {
                LinkedSystem.accLinkingOn(AccountWidgetAccountsController.Account.AcctNumber);
            }
        };
        return AccountMenuItemsHelper.createAccountMenuItems(AccountWidgetAccountsController.Account, accountClickEvent);
    }
    getPanelsMenuItems() {
        const items = [];
        if (CustomerAccess.panelAllowed(PanelNames.AssetBalancesPanel)) {
            items.push({
                type: NewContextMenuItemType.Default,
                icon: MenuIcons.AccountStatistics,
                text: PANEL_ASSET_BALANCES_KEY,
                event: () => {
                    MainWindowManager.Factory.addPanel(PanelNames.AssetBalancesPanel);
                }
            });
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_RESERVER_WITHDRAWAL) &&
            !Resources.isHidden('ribbon.tools.withdrawal')) {
            items.push({
                type: NewContextMenuItemType.Default,
                icon: MenuIcons.WithdrawalIcon,
                text: 'ribbon.tools.withdrawal',
                event: () => {
                    WithdrawalScreen.show();
                }
            });
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_REPORT) && !Resources.isHidden('ribbon.tools.reports')) {
            items.push({
                type: NewContextMenuItemType.Default,
                icon: MenuIcons.ReportsIcon,
                text: 'ribbon.tools.reports',
                event: () => {
                    MainWindowManager.Factory.addPanel(PanelNames.ReportsPanel);
                }
            });
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_PRODUCTS) && !Resources.isHidden('ribbon.tools.products')) {
            items.push({
                type: NewContextMenuItemType.Default,
                icon: MenuIcons.SubscriptionsIcon,
                text: Resources.getResource('ribbon.tools.products'),
                event: () => {
                    MainWindowManager.Factory.addPanel(PanelNames.ProductsPanel);
                }
            });
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_COPY_TRADE) && !Resources.isHidden('panel.CopyTrading')) {
            items.push({
                type: NewContextMenuItemType.Default,
                icon: MenuIcons.CopyTrading,
                text: 'panel.CopyTrading',
                event: () => {
                    MainWindowManager.Factory.addPanel(PanelNames.CopyTradingPanel);
                }
            });
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_WEBHOOKS) && !Resources.isHidden('screen.Webhooks')) {
            items.push({
                type: NewContextMenuItemType.Default,
                icon: MenuIcons.Webhooks,
                text: 'screen.Webhooks',
                event: () => {
                    MainWindowManager.Factory.addScreen(ScreensNames.WebhooksScreen);
                }
            });
        }
        if (DataCache.isAllowedForMyUser(RulesSet.ALLOW_CHANGE_PASSWORD)) {
            items.push({ type: NewContextMenuItemType.Separator });
            if (TradingLockUtils.IsUseLockTradingByPassword && !Resources.isHidden('ribbon.changeTradingPass')) {
                // #89340 & #94446
                items.push({
                    type: NewContextMenuItemType.Default,
                    icon: MenuIcons.ChangeTradingPassword,
                    text: 'screen.changeTradingPass.title',
                    event: () => {
                        TerceraChangePasswordScreen.show(null, null, null, true);
                    }
                });
            }
            items.push({
                type: NewContextMenuItemType.Default,
                icon: MenuIcons.ChangePasswordIcon,
                text: 'screen.changepass.title',
                event: () => {
                    TerceraChangePasswordScreen.show();
                }
            });
        }
        return items;
    }
    getExternalLinksMenuItems() {
        const result = [];
        const links = DataCache.ExternalLinksCache.getExtLinks();
        links.forEach((link) => {
            if (externalLinkManager.isTabLink(link) && link.clientTab === 'Account') {
                const toolName = link.getLocalizedToolName();
                result.push({
                    type: NewContextMenuItemType.Default,
                    icon: MenuIcons.ExternalLinkIcon,
                    text: toolName,
                    event: () => {
                        MainWindowManager.MainWindow.onExternalLinkClicked(link);
                    }
                });
            }
        });
        return result;
    }
    onContextMenuInitialized(contextMenuRef) {
        this.contextMenuRef = contextMenuRef;
    }
}
